import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { ChevronLeft, ChevronRight, Calendar, CalendarOff } from 'lucide-react';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import PlannerItemModal from '../../../components/planner/PlannerItemModal';
import { plannerAPI, integrationsAPI, settingsAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';

const VIEW_OPTIONS = [
  { label: 'Day', value: 'timeGridDay' },
  { label: 'Week', value: 'timeGridWeek' },
  { label: 'Month', value: 'dayGridMonth' },
  { label: 'Year', value: 'multiMonthYear' },
];

const COLOR_MAP = {
  blue: '#3b82f6',
  red: '#ef4444',
  green: '#10b981',
  yellow: '#f59e0b',
  purple: '#8b5cf6',
  orange: '#f97316',
  gray: '#6b7280',
};

const GOOGLE_CALENDAR_COLOR = '#4285F4';

const TAB_CALENDAR = 'calendar';
const TAB_PLANNER = 'planner';

export default function Planner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();
  const calendarRef = useRef(null);
  const [items, setItems] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('dayGridMonth');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [draftSelection, setDraftSelection] = useState(null);
  const [currentTitle, setCurrentTitle] = useState('');
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [integrationStatusLoading, setIntegrationStatusLoading] = useState(true);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = sessionStorage.getItem('planner_active_tab');
      return stored === TAB_PLANNER ? TAB_PLANNER : TAB_CALENDAR;
    } catch {
      return TAB_CALENDAR;
    }
  });
  const toastFired = useRef({ connected: false, error: false });

  const showGoogleEvents = googleConnected && activeTab === TAB_CALENDAR;

  const events = useMemo(() => {
    const plannerEventList = items.map((item) => {
      const colorValue = COLOR_MAP[item.color] || COLOR_MAP.blue;
      return {
        id: item.id,
        title: item.title,
        start: item.start_at,
        end: item.end_at,
        allDay: item.all_day,
        backgroundColor: colorValue,
        borderColor: colorValue,
        textColor: '#ffffff',
        classNames: item.is_done ? ['opacity-70', 'line-through'] : [],
        extendedProps: item,
      };
    });
    if (!showGoogleEvents || !googleEvents.length) return plannerEventList;
    const googleEventList = googleEvents.map((ev) => ({
      id: ev.id,
      title: ev.title,
      start: ev.start,
      end: ev.end,
      allDay: ev.allDay ?? false,
      backgroundColor: ev.backgroundColor ?? GOOGLE_CALENDAR_COLOR,
      borderColor: ev.backgroundColor ?? GOOGLE_CALENDAR_COLOR,
      textColor: '#ffffff',
      classNames: ['fc-google-event'],
      extendedProps: { ...ev, source: 'google' },
    }));
    return [...plannerEventList, ...googleEventList];
  }, [items, googleEvents, showGoogleEvents]);

  const fetchItems = async (range, opts = {}) => {
    if (!range?.startStr || !range?.endStr) return;
    const includeGoogle = opts.includeGoogle ?? showGoogleEvents;
    try {
      setLoading(true);
      const data = await plannerAPI.getItems({
        from: range.startStr,
        to: range.endStr,
        includeGoogle,
      });
      setItems(data.items || []);
      setGoogleEvents(data.googleEvents || []);
    } catch (error) {
      console.error('Failed to load planner items', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatesSet = (arg) => {
    fetchItems({ startStr: arg.startStr, endStr: arg.endStr });
    setCurrentTitle(arg.view.title);
  };

  const openCreateModal = (selection) => {
    setSelectedItem(null);
    setDraftSelection(selection || null);
    setModalOpen(true);
  };

  const handleSelect = (selectionInfo) => {
    openCreateModal({
      startAt: selectionInfo.startStr,
      endAt: selectionInfo.endStr,
      allDay: selectionInfo.allDay,
    });
  };

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps?.source === 'google') return;
    setSelectedItem(clickInfo.event.extendedProps);
    setDraftSelection(null);
    setModalOpen(true);
  };

  const handleSave = async (payload) => {
    if (selectedItem) {
      const updated = await plannerAPI.updateItem(selectedItem.id, payload);
      setItems((prev) =>
        prev.map((item) => (item.id === selectedItem.id ? updated.item : item))
      );
      setSelectedItem(updated.item);
    } else {
      const created = await plannerAPI.createItem(payload);
      setItems((prev) => [...prev, created.item]);
    }
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    await plannerAPI.deleteItem(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    setModalOpen(false);
  };

  const handleToggleDone = async (id) => {
    const updated = await plannerAPI.toggleDone(id);
    setItems((prev) =>
      prev.map((item) => (item.id === id ? updated.item : item))
    );
    setSelectedItem(updated.item);
  };

  const handleAcceptAssignment = async (id) => {
    const updated = await plannerAPI.acceptAssignment(id);
    setItems((prev) => {
      const exists = prev.some((item) => item.id === updated.item.id);
      if (exists) {
        return prev.map((item) => (item.id === updated.item.id ? updated.item : item));
      }
      return [...prev, updated.item];
    });
    setSelectedItem(updated.item);
    setModalOpen(false);
  };

  const handleRejectAssignment = async (id) => {
    await plannerAPI.rejectAssignment(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    setModalOpen(false);
  };

  const handleViewChange = (viewName) => {
    setActiveView(viewName);
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView(viewName);
    }
  };

  const handlePrev = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.prev();
    }
  };

  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.next();
    }
  };

  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.today();
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const { authUrl } = await integrationsAPI.getGoogleAuthUrl();
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        toastError('Connect failed', 'Could not get Google Calendar authorization URL.');
      }
    } catch (err) {
      toastError('Connect failed', err?.message || 'Could not connect Google Calendar.');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      setDisconnectLoading(true);
      await integrationsAPI.disconnectGoogle();
      setGoogleConnected(false);
      setGoogleEvents([]);
      toastSuccess('Disconnected', 'Google Calendar has been disconnected.');
      const api = calendarRef.current?.getApi();
      if (api) {
        fetchItems({ startStr: api.view.activeStart.toISOString(), endStr: api.view.activeEnd.toISOString() });
      }
    } catch (err) {
      toastError('Disconnect failed', err?.message || 'Could not disconnect Google Calendar.');
    } finally {
      setDisconnectLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setIntegrationStatusLoading(true);
    Promise.all([integrationsAPI.getGoogleStatus(), settingsAPI.getGoogleCalendarIntegration()])
      .then(([status, config]) => {
        if (!cancelled) {
          setGoogleConnected(!!status?.connected);
          const configured = !!(config?.hasClientId && config?.hasClientSecret);
          setGoogleConfigured(configured);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleConnected(false);
          setGoogleConfigured(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIntegrationStatusLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const connected = searchParams.get('google_connected');
    const errMsg = searchParams.get('google_error');
    if (connected === '1' && !toastFired.current.connected) {
      toastFired.current.connected = true;
      toastSuccess('Google Calendar connected', 'Your Google Calendar events will appear in the Calendar tab.');
      setGoogleConnected(true);
      setActiveTab(TAB_CALENDAR);
      try {
        sessionStorage.setItem('planner_active_tab', TAB_CALENDAR);
      } catch {}
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('google_connected');
        return next;
      }, { replace: true });
      const api = calendarRef.current?.getApi();
      if (api) {
        fetchItems({ startStr: api.view.activeStart.toISOString(), endStr: api.view.activeEnd.toISOString() }, { includeGoogle: true });
      }
    }
    if (errMsg && !toastFired.current.error) {
      toastFired.current.error = true;
      toastError('Google connection failed', decodeURIComponent(errMsg));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('google_error');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, toastSuccess, toastError]);

  useEffect(() => {
    try {
      sessionStorage.setItem('planner_active_tab', activeTab);
    } catch {}
  }, [activeTab]);

  const tabChangeRef = useRef(true);
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (tabChangeRef.current) {
      tabChangeRef.current = false;
      return;
    }
    fetchItems({ startStr: api.view.activeStart.toISOString(), endStr: api.view.activeEnd.toISOString() });
  }, [activeTab, showGoogleEvents]);

  return (
    <Layout>
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Planner</h1>
            <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
              Schedule tasks, events, and reminders for your day.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
            {!integrationStatusLoading && googleConfigured && !googleConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectGoogle}
                className="h-8 gap-1.5"
                aria-label="Connect Google Calendar"
              >
                <Calendar className="h-4 w-4" />
                Connect Google Calendar
              </Button>
            )}
            {googleConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnectGoogle}
                disabled={disconnectLoading}
                className="h-8 gap-1.5 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                aria-label="Disconnect Google Calendar"
              >
                {disconnectLoading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <CalendarOff className="h-4 w-4" />
                )}
                Disconnect Google Calendar
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => openCreateModal()}
              className="h-8"
            >
              New Item
            </Button>
          </div>
        </div>

        {googleConnected && (
          <div className="flex gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-1">
            <button
              type="button"
              onClick={() => setActiveTab(TAB_CALENDAR)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === TAB_CALENDAR
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(TAB_PLANNER)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === TAB_PLANNER
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Planner
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {VIEW_OPTIONS.map((view) => (
            <Button
              key={view.value}
              variant={activeView === view.value ? 'default' : 'outline'}
              size="sm"
              className="h-8 text-xs"
              onClick={() => handleViewChange(view.value)}
            >
              {view.label}
            </Button>
          ))}
        </div>

        <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)] p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className="h-8 w-8 p-0 text-xs"
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                className="h-8 w-8 p-0 text-xs"
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToday}
                className="h-8 ml-2 text-xs"
              >
                Today
              </Button>
            </div>
            <h2 className="text-h2 text-[var(--ds-text-primary)] font-semibold">
              {currentTitle}
            </h2>
            <div className="w-24"></div>
          </div>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
            initialView="dayGridMonth"
            headerToolbar={false}
            height="auto"
            selectable
            selectMirror
            events={events}
            select={handleSelect}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            dayMaxEvents
            nowIndicator
          />
        </div>
      </div>

      <PlannerItemModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        item={selectedItem}
        draftSelection={draftSelection}
        onSave={handleSave}
        onDelete={handleDelete}
        onToggleDone={handleToggleDone}
        onAcceptAssignment={handleAcceptAssignment}
        onRejectAssignment={handleRejectAssignment}
      />
    </Layout>
  );
}
